import { queryKeys } from '@query/keys'
import type { QueryClient, QueryKey } from '@tanstack/react-query'

import { TransactionType } from '@utils/transaction.utils'

import { TransactionStatus } from '@model/TransactionStatus'
import { WalletTypeEnum } from '@model/WalletDto'

import { TransactionEvent } from './transactionEvent'
import type { WebSocketTransactionEvent } from './types'

const getQueryKeyPrefix = (queryKey: QueryKey): QueryKey => {
  const prefix = [...queryKey]

  while (prefix[prefix.length - 1] === null) {
    prefix.pop()
  }

  return prefix
}

const TRANSACTION_HISTORY_REFRESH_KEYS: QueryKey[] = [
  getQueryKeyPrefix(queryKeys.transactionHistory()),
  getQueryKeyPrefix(queryKeys.paymentAgent.transactionHistory()),
]

const IB_TRANSACTION_HISTORY_REFRESH_KEYS: QueryKey[] = [
  getQueryKeyPrefix(queryKeys.ib.transactionHistory()),
  getQueryKeyPrefix(queryKeys.wallet.withTransactions()),
]

const WALLET_BALANCE_REFRESH_KEYS: QueryKey[] = [
  getQueryKeyPrefix(queryKeys.wallet.list()),
  getQueryKeyPrefix(queryKeys.wallet.byType()),
  getQueryKeyPrefix(queryKeys.walletExtended.totalBalance()),
]

const IB_WALLET_BALANCE_REFRESH_KEYS: QueryKey[] = [queryKeys.ib.wallets()]

const TRADING_ACCOUNT_BALANCE_REFRESH_KEYS: QueryKey[] = [
  queryKeys.tradingAccount.totalsAll(),
  queryKeys.tradingAccount.balances(),
  getQueryKeyPrefix(queryKeys.tradingAccount.live()),
  getQueryKeyPrefix(queryKeys.tradingAccount.inactiveLive()),
]

const REFRESHED_TRANSACTION_TYPES = new Set<TransactionType>([
  TransactionType.WalletTransfer,
  TransactionType.DepositFromPaymentAgent,
  TransactionType.WithdrawToPaymentAgent,
  TransactionType.WalletDeposit,
  TransactionType.WalletWithdrawal,
  TransactionType.TransferToTradingAccount,
  TransactionType.TransferFromTradingAccount,
  TransactionType.TradingAccountBalanceConversion,
  TransactionType.InactivityTransfer,
  TransactionType.Bonus,
])

const TRADING_ACCOUNT_BALANCE_TRANSACTION_TYPES = new Set<TransactionType>([
  TransactionType.TransferToTradingAccount,
  TransactionType.TradingAccountBalanceConversion,
  TransactionType.TransferFromTradingAccount,
])

const BALANCE_REFRESH_EVENTS = new Set<TransactionEvent>([
  TransactionEvent.Changed,
  TransactionEvent.Reversed,
  TransactionEvent.Completed,
])

const shouldRefreshBalances = (event: WebSocketTransactionEvent): boolean =>
  BALANCE_REFRESH_EVENTS.has(event.type) ||
  (event.type === TransactionEvent.Created && event.status === TransactionStatus.Completed)

const affectsWalletType = (event: WebSocketTransactionEvent, walletType: WalletTypeEnum): boolean =>
  event.walletTypes?.includes(walletType) ?? false

const affectsStandardTransactionHistory = (event: WebSocketTransactionEvent): boolean =>
  event.kind !== undefined && REFRESHED_TRANSACTION_TYPES.has(event.kind)

const affectsIBTransactionHistory = (event: WebSocketTransactionEvent): boolean =>
  affectsWalletType(event, WalletTypeEnum.IB)

const shouldRefreshTransactionData = (event: WebSocketTransactionEvent): boolean =>
  affectsStandardTransactionHistory(event) || affectsIBTransactionHistory(event)

const affectsTradingAccountBalance = (event: WebSocketTransactionEvent): boolean =>
  event.kind !== undefined && TRADING_ACCOUNT_BALANCE_TRANSACTION_TYPES.has(event.kind)

const getTransactionHistoryRefreshKeys = (event: WebSocketTransactionEvent): QueryKey[] => [
  ...(affectsStandardTransactionHistory(event) ? TRANSACTION_HISTORY_REFRESH_KEYS : []),
  ...(affectsIBTransactionHistory(event) ? IB_TRANSACTION_HISTORY_REFRESH_KEYS : []),
]

const getWalletBalanceRefreshKeys = (event: WebSocketTransactionEvent): QueryKey[] => [
  ...(affectsStandardTransactionHistory(event) || affectsIBTransactionHistory(event)
    ? WALLET_BALANCE_REFRESH_KEYS
    : []),
  ...(affectsIBTransactionHistory(event) ? IB_WALLET_BALANCE_REFRESH_KEYS : []),
]

const refreshQueryGroups = (queryClient: QueryClient, keys: QueryKey[]): void => {
  keys.forEach((queryKey) => {
    queryClient.invalidateQueries({ queryKey, refetchType: 'all' })
  })
}

const refreshTransactionDetails = (
  queryClient: QueryClient,
  event: WebSocketTransactionEvent
): void => {
  if (!event.transactionId) {
    queryClient.invalidateQueries({
      queryKey: getQueryKeyPrefix(queryKeys.transaction.statusHistory()),
    })
    return
  }

  queryClient.invalidateQueries({ queryKey: queryKeys.transaction.byId(event.transactionId) })
  queryClient.invalidateQueries({
    queryKey: getQueryKeyPrefix(queryKeys.transaction.statusHistory(event.transactionId)),
  })
}

const refreshTransactionHistory = (
  queryClient: QueryClient,
  event: WebSocketTransactionEvent
): void => {
  refreshQueryGroups(queryClient, getTransactionHistoryRefreshKeys(event))
  refreshTransactionDetails(queryClient, event)
}

const refreshBalances = (queryClient: QueryClient, event: WebSocketTransactionEvent): void => {
  if (shouldRefreshTransactionData(event)) {
    refreshQueryGroups(queryClient, getWalletBalanceRefreshKeys(event))
  }

  if (affectsTradingAccountBalance(event)) {
    refreshQueryGroups(queryClient, TRADING_ACCOUNT_BALANCE_REFRESH_KEYS)
  }

  if (event.kind === TransactionType.Bonus) {
    queryClient.invalidateQueries({
      queryKey: getQueryKeyPrefix(queryKeys.walletExtended.activeBonus(event.walletId)),
    })
  }
}

export const refreshTransactionEventData = (
  queryClient: QueryClient,
  event: WebSocketTransactionEvent
): void => {
  if (!shouldRefreshTransactionData(event)) return

  refreshTransactionHistory(queryClient, event)

  if (!shouldRefreshBalances(event)) return

  refreshBalances(queryClient, event)
}
